import { Box, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { Button, ChevronDownIcon, ChevronUpIcon } from '#features/ui';
import { OAuthProvider, OAuthProviders } from '../../constants/providers';
import { useAppleSignInAvailable } from '../../hooks/useAppleSignInAvailable';
import { AppleSignInButton } from '../AppleSignInButton';
import { AuthButton } from '../AuthButton';

const primaryProviders = [OAuthProvider.GOOGLE, OAuthProvider.DISCORD];
const otherProviders = [OAuthProvider.STEAM, OAuthProvider.EPIC_GAMES, OAuthProvider.RIOT_GAMES];

type AuthMethodsProps = {
  mode: 'sign-in' | 'sign-up';
  children: ReactNode;
  emailError?: boolean;
  emailBusy?: boolean;
};

/** The selection changes only presentation; all providers keep their existing auth flow. */
export const AuthMethods = ({ mode, children, emailError, emailBusy }: AuthMethodsProps) => {
  const { t } = useLingui();
  const { available: appleAvailable } = useAppleSignInAvailable();
  const id = useId();
  const [emailOpen, setEmailOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);

  useEffect(() => {
    if (emailError) setEmailOpen(true);
  }, [emailError]);

  const providerButton = (provider: OAuthProvider) => {
    const providerName = OAuthProviders[provider].fullName;
    return (
      <AuthButton
        key={provider}
        provider={provider}
        label={
          mode === 'sign-in' ? t`Sign in with ${providerName}` : t`Sign up with ${providerName}`
        }
        variant="outline"
        color="fg"
      />
    );
  };

  return (
    <Stack gap={4}>
      {mode === 'sign-in' ? (
        <Text textStyle="caption" color="fg.muted" textAlign="center">
          {t`Use the method you used before.`}
        </Text>
      ) : null}
      <Stack gap={3}>
        {appleAvailable ? <AppleSignInButton mode={mode} /> : null}
        {primaryProviders.map(providerButton)}
      </Stack>
      <Stack gap={3}>
        <Button
          type="button"
          variant="ghost"
          color="fg.link"
          minH={11}
          h="auto"
          py={2}
          whiteSpace="normal"
          w="100%"
          position="relative"
          px={12}
          textAlign="center"
          aria-expanded={emailOpen}
          aria-controls={`${id}-email`}
          disabled={emailBusy}
          onClick={(event) => {
            if (emailOpen) event.currentTarget.focus();
            setEmailOpen(!emailOpen);
          }}
        >
          <Box as="span" minW={0}>
            {mode === 'sign-in' ? t`Sign in with email` : t`Sign up with email`}
          </Box>
          <Box position="absolute" insetInlineEnd={4}>
            {emailOpen ? (
              <ChevronUpIcon style={{ flexShrink: 0 }} />
            ) : (
              <ChevronDownIcon style={{ flexShrink: 0 }} />
            )}
          </Box>
        </Button>
        {/* Keep fields mounted to preserve this page's input; hidden fields leave the tab order. */}
        <Box id={`${id}-email`} hidden={!emailOpen}>
          {children}
        </Box>
        <Button
          type="button"
          variant="ghost"
          color="fg.link"
          minH={11}
          w="100%"
          h="auto"
          py={2}
          whiteSpace="normal"
          position="relative"
          px={12}
          textAlign="center"
          aria-expanded={otherOpen}
          aria-controls={`${id}-other`}
          onClick={(event) => {
            if (otherOpen) event.currentTarget.focus();
            setOtherOpen(!otherOpen);
          }}
        >
          <Box as="span" minW={0}>
            {mode === 'sign-in' ? t`Other ways to sign in` : t`Other ways to sign up`}
          </Box>
          <Box position="absolute" insetInlineEnd={4}>
            {otherOpen ? (
              <ChevronUpIcon style={{ flexShrink: 0 }} />
            ) : (
              <ChevronDownIcon style={{ flexShrink: 0 }} />
            )}
          </Box>
        </Button>
        {/* OAuth hooks remain mounted, including on an error callback with a provider parameter. */}
        <Box id={`${id}-other`} hidden={!otherOpen}>
          <Stack gap={3}>{otherProviders.map(providerButton)}</Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
